import type { ProposalLetter, SalesInternalOrder, SalesOrderCreate } from '@veem/crm-schema'
import type { Deal, DealLostReason, ProposalStatus } from '@veem/schema'
import { showToast } from '@veem/ui'
import type { Snapshot } from '../../app.tsx'
import { api, reportRefreshFailure } from '../../lib/api.ts'
import { copySnapshot } from '../../lib/snapshot-value.ts'
import type { ProtoCustomer, ProtoPerson } from '../../prototype/data/customers.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import type {
  EngineeringSource,
  OrderLine,
  ProductionPriority,
} from '../../prototype/data/orders.ts'
import { type CustomerInput, proto } from '../../prototype/state/store.ts'
import { upload } from '../engineering/actions.ts'

const pending = new Set<string>()
const versionOf = (record: unknown) => (record as { version: number } | undefined)?.version ?? 1
const orderVersion = (id: string) => versionOf(proto.orderById(id)?.deal)
const customerVersion = (id: string) =>
  versionOf(proto.state.customers.find((row) => row.id === id))
const refresh = async () => {
  const snapshot = copySnapshot(await api<Snapshot>('/snapshot'))
  hydrateData(snapshot.data)
  proto.hydrate(snapshot.state)
}
const write = async (path: string, body: unknown, method = 'POST', key = path) => {
  if (pending.has(key)) {
    throw new Error('Дочекайтеся збереження попередньої зміни')
  }
  pending.add(key)
  try {
    const result = await api<{ id: string; version: number; personId?: string }>(path, {
      method,
      body: JSON.stringify(body),
    })
    try {
      await refresh()
    } catch {
      reportRefreshFailure()
    }
    return result
  } finally {
    pending.delete(key)
  }
}
export const salesFeedback = async (action: Promise<unknown>, message?: string) => {
  try {
    await action
    if (message) showToast(message)
    return true
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Не вдалося зберегти зміни', 'error')
    return false
  }
}
const orderWrite = (id: string, path: string, body: object, method = 'POST') =>
  write(`/orders/${id}${path}`, { version: orderVersion(id), ...body }, method, `order:${id}`)
const customerWrite = (id: string, path: string, body: object, method = 'POST') =>
  write(
    `/customers/${id}${path}`,
    { version: customerVersion(id), ...body },
    method,
    `customer:${id}`,
  )
export const salesActions = {
  createCustomer: async (input: CustomerInput) => (await write('/customers', input)).id,
  updateCustomer: (id: string, patch: Partial<ProtoCustomer>, version?: number) =>
    customerWrite(id, '', { patch, ...(version ? { version } : {}) }, 'PATCH'),
  createCustomerCategory: (name: string, parentId: string | null) =>
    write('/customers/categories', { name, parentId }),
  updateCustomerCategory: (id: string, name: string, version: number) =>
    write(`/customers/categories/${encodeURIComponent(id)}`, { name, version }, 'PATCH'),
  archiveCustomerCategory: (id: string, archived: boolean, version: number) =>
    write(`/customers/categories/${encodeURIComponent(id)}/archive`, { archived, version }),
  addContact: (
    id: string,
    contact: {
      personId?: string
      fullName: string
      role: string
      phone: string | null
      email: string | null
    },
  ) => customerWrite(id, '/contacts', { contact }),
  updatePerson: (id: string, patch: Partial<ProtoPerson>) =>
    write(
      `/customers/contacts/${id}`,
      { version: versionOf(proto.state.persons.find((row) => row.id === id)), patch },
      'PATCH',
    ),
  updateLinkRole: (id: string, personId: string, role: string) =>
    customerWrite(id, '/contact-role', { personId, role }),
  setPrimaryContact: (id: string, personId: string) =>
    customerWrite(id, '/primary-contact', { personId }),
  detachContact: (id: string, personId: string) =>
    customerWrite(id, '/detach-contact', { personId }),
  createOrder: async (input: SalesOrderCreate) => (await write('/orders', input)).id,
  updateDeal: (
    id: string,
    patch: Partial<
      Pick<Deal, 'name' | 'managerId' | 'sellerEntityId' | 'expectedCloseAt' | 'internalComment'>
    >,
    version?: number,
  ) => orderWrite(id, '', { patch, ...(version ? { version } : {}) }, 'PATCH'),
  toggleDealContact: (id: string, personId: string) =>
    orderWrite(id, '/contact', { personId }, 'PATCH'),
  setLines: (id: string, lines: OrderLine[], version?: number) =>
    orderWrite(
      id,
      '/lines',
      {
        ...(version ? { version } : {}),
        lines: lines.map((line) => ({
          id: line.id,
          productId: line.productId,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
        })),
      },
      'PATCH',
    ),
  setStage: (id: string, stageKey: string) => orderWrite(id, '/stage', { stageKey }),
  loseDeal: (id: string, reason: DealLostReason, note: string | null, stageKey?: string) =>
    orderWrite(id, '/lose', { reason, note, ...(stageKey ? { stageKey } : {}) }),
  reopenDeal: (id: string, stageKey: string) => orderWrite(id, '/reopen', { stageKey }),
  createProposal: (id: string, letter?: ProposalLetter) =>
    write(
      `/order-proposals/${id}`,
      { version: orderVersion(id), ...(letter ? { letter } : {}) },
      'POST',
      `order:${id}`,
    ),
  setProposalStatus: (id: string, proposalId: string, status: ProposalStatus) =>
    write(
      `/order-proposals/${id}/status`,
      { version: orderVersion(id), proposalId, status },
      'PATCH',
      `order:${id}`,
    ),
  sendToProduction: (
    id: string,
    engineerId: string | null,
    stageKey?: string,
    stageKeys?: string[],
    needsEngineer = false,
  ) =>
    orderWrite(id, '/production', {
      engineerId,
      needsEngineer,
      ...(stageKey ? { stageKey } : {}),
      ...(stageKeys?.length ? { stageKeys } : {}),
    }),
  requestEngineering: async (
    id: string,
    engineerId: string | null,
    note: string,
    dueAt: string,
    priority: ProductionPriority = 'medium',
    files: File[] = [],
    source: EngineeringSource = { kind: 'order' },
  ) => {
    await upload(
      `/order-engineering/${id}`,
      { version: orderVersion(id), engineerId, note, dueAt, priority, source },
      files,
      'POST',
    )
    try {
      await refresh()
    } catch {
      reportRefreshFailure()
    }
    return id
  },
  setInternalOrderPlan: async (id: string, version: number, inPlan: boolean) => {
    try {
      await api(`/internal-orders/${encodeURIComponent(id)}/plan`, {
        method: 'POST',
        body: JSON.stringify({ version, inPlan }),
      })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Не вдалося зберегти зміни', 'error')
      return false
    }
    try {
      await refresh()
    } catch {
      reportRefreshFailure()
    }
    return true
  },
  cancelInternalOrder: async (id: string, version: number) => {
    try {
      await api(`/internal-orders/${encodeURIComponent(id)}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ version }),
      })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Не вдалося скасувати', 'error')
      return false
    }
    try {
      await refresh()
    } catch {
      reportRefreshFailure()
    }
    return true
  },
  createInternalOrder: async (input: SalesInternalOrder & { files?: File[] }) => {
    const { files = [], ...body } = input
    const result = await upload<{ id: string }>('/internal-orders', body, files, 'POST')
    try {
      await refresh()
    } catch {
      reportRefreshFailure()
    }
    return result.id
  },
}
